import Phaser from 'phaser';
import { V2State, computeFirstGids } from '../../../lib/mapV2';
import { useCameraSettingsStore } from '../../../state/cameraSettings';
import type { MainSceneLike } from '../../types/scene';
import { DEPTH_GROUND, DEPTH_WALLS, DEPTH_COLLISION } from './depthConstants';

export class SceneInitializer {
  static initializeMap(scene: MainSceneLike): {
    mapRef: Phaser.Tilemaps.Tilemap;
    editorGround: Phaser.Tilemaps.TilemapLayer;
    wallsLayer: Phaser.Tilemaps.TilemapLayer;
    collisionLayer: Phaser.Tilemaps.TilemapLayer;
    dynamicTilesets: Map<string, Phaser.Tilemaps.Tileset>;
    v2: { state: V2State; firstGids: number[]; chunkSize: number };
  } {
    const pre = window.__v2_state;
    if (!pre || !pre.mapMeta.width) throw new Error('Missing V2 state in MainScene');

    const tilemapConfig: Phaser.Types.Tilemaps.TilemapConfig = {};
    if (pre.mapMeta.width != null) tilemapConfig.width = pre.mapMeta.width;
    if (pre.mapMeta.height != null) tilemapConfig.height = pre.mapMeta.height;
    if (pre.mapMeta.tileWidth != null) tilemapConfig.tileWidth = pre.mapMeta.tileWidth;
    if (pre.mapMeta.tileHeight != null) tilemapConfig.tileHeight = pre.mapMeta.tileHeight;
    const map = scene.make.tilemap(tilemapConfig);

    // Compute firstGids based on texture dimensions BEFORE registering tilesets,
    // so we can pass them explicitly to addTilesetImage for GID alignment.
    const firstGids = computeFirstGids(pre.tilesetRegistry, scene);

    const sorted = [...pre.tilesetRegistry].sort((a, b) => a.slot - b.slot);
    const dynamicTilesets = new Map<string, Phaser.Tilemaps.Tileset>();
    for (const ts of sorted) {
      try {
        const fg = firstGids[ts.slot];
        const phTs = map.addTilesetImage(
          ts.key,
          ts.key,
          ts.tileWidth,
          ts.tileHeight,
          ts.margin ?? 0,
          ts.spacing ?? 0,
          fg,
        );
        if (phTs) dynamicTilesets.set(ts.key, phTs);
      } catch {}
    }

    const allTs = Array.from(dynamicTilesets.values());
    // Phaser requires a non-empty tileset list. When no tilesets are registered
    // yet we pass a single-element placeholder array; Phaser ignores undefined
    // entries when resolving tile data, so the layers remain usable until
    // tilesets are registered later. The cast confines the workaround to a
    // narrow surface (`Tileset[]`).
    const tilesets: Phaser.Tilemaps.Tileset[] =
      allTs.length > 0 ? allTs : ([undefined] as unknown as Phaser.Tilemaps.Tileset[]);
    const editorGround = map.createBlankLayer(
      'Ground',
      tilesets,
      0,
      0,
      pre.mapMeta.width ?? undefined,
      pre.mapMeta.height ?? undefined,
      pre.mapMeta.tileWidth ?? undefined,
      pre.mapMeta.tileHeight ?? undefined,
    );
    const wallsLayer = map.createBlankLayer(
      'Walls',
      tilesets,
      0,
      0,
      pre.mapMeta.width ?? undefined,
      pre.mapMeta.height ?? undefined,
      pre.mapMeta.tileWidth ?? undefined,
      pre.mapMeta.tileHeight ?? undefined,
    );
    const collisionLayer = map.createBlankLayer(
      'Collision',
      tilesets,
      0,
      0,
      pre.mapMeta.width ?? undefined,
      pre.mapMeta.height ?? undefined,
      pre.mapMeta.tileWidth ?? undefined,
      pre.mapMeta.tileHeight ?? undefined,
    );
    // Phaser typings declare these as `TilemapLayer | null`. In practice they
    // succeed because `createBlankLayer` only returns null when the tileset
    // list is invalid, which the placeholder above avoids.
    if (!editorGround || !wallsLayer || !collisionLayer) {
      throw new Error('SceneInitializer: createBlankLayer returned null');
    }

    // Fixed tile layers sit below the y-sort band (which starts at 0), so
    // furniture and actors render above the floor and walls (Strang C). Wall
    // occlusion of actors is out of scope (C-DP3): the wall layer is flat.
    editorGround.setDepth(DEPTH_GROUND);
    wallsLayer.setDepth(DEPTH_WALLS);
    collisionLayer.setDepth(DEPTH_COLLISION);

    // SPACE WORK interactive operational panels.
    if (
      pre.mapMeta.width === 80 &&
      pre.mapMeta.height === 52
    ) {
      const panelScene =
        scene as unknown as Phaser.Scene;

      const hotspot = (
        x: number,
        y: number,
        width: number,
        height: number,
        id: string,
      ) => {
        const area =
          panelScene.add.rectangle(
            x + width / 2,
            y + height / 2,
            width,
            height,
            0xffffff,
            0.001,
          );

        area
          .setDepth(DEPTH_WALLS + 50)
          .setInteractive({
            useHandCursor: true,
          });

        area.on(
          "pointerover",
          () => {
            area.setFillStyle(
              0xc8203b,
              0.12,
            );
          },
        );

        area.on(
          "pointerout",
          () => {
            area.setFillStyle(
              0xffffff,
              0.001,
            );
          },
        );

        area.on(
          "pointerdown",
          () => {
            window.dispatchEvent(
              new CustomEvent(
                "space-work:open-panel",
                {
                  detail: { id },
                },
              ),
            );
          },
        );
      };

      hotspot(
        116,162,
        320,31,
        "commercial",
      );

      hotspot(
        116,402,
        320,31,
        "sdr",
      );

      hotspot(
        1134,262,
        140,31,
        "executive",
      );
    }


    // ==================================================
    // SPACE WORK V3 — HQ 80x52
    // ==================================================
    const isSpaceWorkV3 =
      pre.mapMeta.width === 80 &&
      pre.mapMeta.height === 52;

    if (isSpaceWorkV3) {

      const ps =
        scene as unknown as Phaser.Scene;

      ps.cameras.main
        .setBackgroundColor(
          0x0d121a
        );

      editorGround
        .setTint(
          0x73757a
        );

      const g=
        ps.add.graphics();

      g.setDepth(
        DEPTH_GROUND + 1
      );


      // Full office floor.
      g.fillStyle(
        0x151a21,
        0.92
      );

      g.fillRect(
        32,64,
        1216,736
      );


      const room=(
        x:number,
        y:number,
        w:number,
        h:number,
        fill:number,
      )=>{

        g.fillStyle(
          fill,
          0.96
        );

        g.fillRoundedRect(
          x,y,w,h,4
        );

        g.lineStyle(
          2,
          0x514b43,
          1
        );

        g.strokeRoundedRect(
          x,y,w,h,4
        );

        g.fillStyle(
          0xc51f3a,
          1
        );

        g.fillRect(
          x,
          y,
          w,
          3
        );
      };


      // Departments.
      room(
        64,96,
        352,208,
        0x222a35
      );

      room(
        64,336,
        352,304,
        0x202832
      );

      room(
        448,96,
        224,176,
        0x252c35
      );

      room(
        448,320,
        224,192,
        0x1f2932
      );

      // Executive rooms.
      room(
        704,96,
        160,176,
        0x25272c
      );

      room(
        896,96,
        160,176,
        0x25272c
      );

      room(
        1088,96,
        160,176,
        0x2b2527
      );

      // Social.
      room(
        448,560,
        224,176,
        0x34291f
      );

      room(
        704,560,
        256,176,
        0x253029
      );

      // Meeting.
      room(
        992,320,
        256,416,
        0x232a34
      );


      // Main circulation.
      g.fillStyle(
        0x10151b,
        1
      );

      g.fillRect(
        416,64,
        32,736
      );

      g.fillRect(
        672,64,
        32,736
      );

      g.fillRect(
        864,64,
        32,256
      );

      g.fillRect(
        1056,64,
        32,256
      );


      const text=(
        x:number,
        y:number,
        value:string,
        size=11,
        color="#f5f5f5",
      )=>
        ps.add.text(
          x,
          y,
          value,
          {
            fontFamily:
              "Arial, sans-serif",
            fontSize:
              size+"px",
            color,
            fontStyle:
              "bold",
            stroke:
              "#090c10",
            strokeThickness:
              3,
          }
        )
        .setOrigin(
          0.5
        )
        .setDepth(
          DEPTH_WALLS + 5
        );



      text(
        240,116,
        "COMERCIAL"
      );

      text(
        240,356,
        "SDR / PRE-VENDAS"
      );

      text(
        560,116,
        "CS / RETENCAO"
      );

      text(
        560,340,
        "PRODUCT / TI"
      );

      text(
        784,116,
        "SALA DO DAVI"
      );

      text(
        976,116,
        "SALA DO FABIO"
      );

      text(
        1168,116,
        "DIRETORIA"
      );

      text(
        560,580,
        "CAFE"
      );

      text(
        832,580,
        "LOUNGE"
      );

      text(
        1120,340,
        "SALA DE REUNIAO"
      );


      // Dashboard shells.
      // No fake metrics: these are only visual targets
      // for the real data integration.
      const panel=(
        x:number,
        y:number,
        w:number,
        title:string,
      )=>{

        g.fillStyle(
          0x090c11,
          0.96
        );

        g.fillRoundedRect(
          x,y,w,30,4
        );

        g.lineStyle(
          1,
          0xc51f3a,
          1
        );

        g.strokeRoundedRect(
          x,y,w,30,4
        );

        text(
          x+w/2,
          y+15,
          title,
          9,
          "#d9dde3"
        );
      };


      panel(
        80,124,
        320,
        "PAINEL COMERCIAL"
      );

      panel(
        80,364,
        320,
        "PAINEL SDR"
      );

      panel(
        1098,224,
        140,
        "VISAO GERAL"
      );
    }


    // SPACE WORK premium theme - experimental map only.
    // Premium Space test map is 60x42.
    // Production Space map remains 50x40.
    const isSpacePremium =
      pre.mapMeta.width === 60 &&
      pre.mapMeta.height === 42;

    if (isSpacePremium) {
      const ps = scene as unknown as Phaser.Scene;

      // Graphite architecture; furniture keeps its original warm wood.
      editorGround.setTint(0x69717c);
      wallsLayer.setTint(0x8a867f);
      ps.cameras.main.setBackgroundColor(0x111722);

      const g = ps.add.graphics();
      g.setDepth(DEPTH_GROUND + 1);

      // Base darkening.
      g.fillStyle(0x171b22, 0.18);
      g.fillRect(72, 104, 800, 480);

      // Departments.
      g.fillStyle(0x202733, 0.48);
      g.fillRect(72, 120, 168, 224);
      g.fillRect(240, 120, 168, 224);
      g.fillRect(584, 120, 288, 144);
      g.fillRect(72, 408, 168, 176);
      g.fillRect(240, 408, 168, 176);
      g.fillRect(584, 360, 288, 224);

      // Central circulation.
      g.fillStyle(0x10141a, 0.58);
      g.fillRect(408, 104, 176, 480);

      // Coffee keeps a warm tone.
      g.fillStyle(0x51361f, 0.34);
      g.fillRect(424, 104, 112, 96);

      // Space red accents.
      g.fillStyle(0xb51d31, 0.92);
      g.fillRect(72, 120, 168, 3);
      g.fillRect(240, 120, 168, 3);
      g.fillRect(584, 120, 288, 3);
      g.fillRect(72, 408, 168, 3);
      g.fillRect(240, 408, 168, 3);
      g.fillRect(584, 360, 288, 3);

      const label = (x: number, y: number, value: string, size = 10) =>
        ps.add
          .text(x, y, value, {
            fontFamily: "Arial, sans-serif",
            fontSize: size + "px",
            color: "#f4f6f8",
            fontStyle: "bold",
            stroke: "#0b0d12",
            strokeThickness: 2,
          })
          .setOrigin(0.5)
          .setDepth(DEPTH_WALLS + 1);

      label(480, 101, "SPACE WORK", 18);
      label(156, 137, "COMERCIAL");
      label(324, 137, "SDR");
      label(728, 137, "GESTAO");
      label(156, 425, "CS / RETENCAO");
      label(324, 425, "LOUNGE");
      label(728, 377, "MEETING");
    }


    try {
      collisionLayer.setVisible(false);
    } catch {}
    try {
      collisionLayer.setCollisionByExclusion([-1], true);
    } catch {}

    const v2 = { state: pre, firstGids, chunkSize: pre.mapMeta.chunkSize };

    const collision = map.addTilesetImage('collision_tiles', 'collision_tiles', 16, 16, 0, 0);
    if (collision) dynamicTilesets.set('collision_tiles', collision);

    scene.physics.world.setBounds(0, 0, map.widthInPixels, map.heightInPixels);

    return { mapRef: map, editorGround, wallsLayer, collisionLayer, dynamicTilesets, v2 };
  }

  static initializeCamera(
    scene: MainSceneLike,
    mapRef: Phaser.Tilemaps.Tilemap,
  ): {
    labelCamera: Phaser.Cameras.Scene2D.Camera;
    labelLayer: Phaser.GameObjects.Layer;
  } {
    // Clean up stale label state from previous scene run (Phaser reuses scene instances)
    const staleLayer = scene.labelLayer ?? undefined;
    const staleCam = scene.labelCamera ?? undefined;
    if (staleCam) {
      try {
        scene.cameras.remove(staleCam, true);
      } catch {
        /* already destroyed */
      }
    }
    if (staleLayer) {
      try {
        staleLayer.destroy();
      } catch {
        /* already destroyed */
      }
    }
    scene.labelLayer = null;
    scene.labelCamera = null;

    const cam = scene.cameras.main;
    cam.setBackgroundColor('#202020');
    cam.setZoom(3);
    cam.setBounds(0, 0, mapRef.widthInPixels, mapRef.heightInPixels);

    // Apply center-camera setting
    const cameraSettings = useCameraSettingsStore.getState().settings;
    if (cameraSettings.centerCamera) {
      cam.removeBounds();
    }

    const labelLayer = scene.add.layer();
    labelLayer.setDepth(10000);
    cam.ignore(labelLayer);

    const labelCam = scene.cameras.add(0, 0, scene.scale.width, scene.scale.height);
    labelCam.setZoom(1);
    labelCam.setScroll(0, 0);
    labelCam.setRoundPixels(true);

    const refreshLabelCamIgnore = () => {
      if (!labelCam || !labelLayer) return;
      const labelMembers = new Set(labelLayer.list);
      labelMembers.add(labelLayer);
      const toIgnore = scene.children.list.filter((o) => !labelMembers.has(o));
      labelCam.ignore(toIgnore);
    };

    refreshLabelCamIgnore();
    scene.events.on(Phaser.Scenes.Events.POST_UPDATE, refreshLabelCamIgnore);

    const onResize = () => {
      const cs = useCameraSettingsStore.getState().settings;
      if (!cs.centerCamera) {
        cam.setBounds(0, 0, mapRef.widthInPixels, mapRef.heightInPixels);
      }
      if (labelCam) labelCam.setSize(scene.scale.width, scene.scale.height);
    };
    scene.scale.on('resize', onResize);

    const unsubCameraSettings = useCameraSettingsStore.subscribe((state) => {
      if (state.settings.centerCamera) {
        cam.removeBounds();
      } else {
        cam.setBounds(0, 0, mapRef.widthInPixels, mapRef.heightInPixels);
      }
    });

    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      unsubCameraSettings();
      // Remove POST_UPDATE listener
      scene.events.off(Phaser.Scenes.Events.POST_UPDATE, refreshLabelCamIgnore);
      // Remove scale resize listener (game-level, not cleaned up by scene shutdown)
      scene.scale.off('resize', onResize);
      // Destroy label camera
      try {
        scene.cameras.remove(labelCam, true);
      } catch {
        /* noop */
      }
      // Destroy label layer (and its children)
      try {
        labelLayer.destroy();
      } catch {
        /* noop */
      }
      // Clear references on scene instance
      scene.labelLayer = null;
      scene.labelCamera = null;
    });

    // Store references on scene instance for defensive cleanup on next run
    scene.labelLayer = labelLayer;
    scene.labelCamera = labelCam;

    return { labelCamera: labelCam, labelLayer };
  }
}
